import { Feature, FeatureCollection } from 'geojson';
import { nanoid } from 'nanoid';
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import styles from "../styles/components/selected-area-layer.module.scss";

import L from 'leaflet';

import {
  GeoJSON,
  Marker,
  Rectangle
} from 'react-leaflet';

import "react-leaflet-fullscreen/styles.css";

import { LocationsContext } from '../context/locations';
import { MapDataContext } from '../context/map-data';
import { LayerTypes } from '../core/global';

import { ModelShape } from '../data/layers';
import { convertGeomToGeoJSON, getItemByKeyValue, getMinMaxValuesFromGeomShape } from '../helpers/core';
import {
  EditableGridBoundsState,
  EditableGridOverlay
} from './rectangle';

export const getLayerFromModel = (modelKey: string | null, currentLayer: LayerTypes | null, dataSources: ModelShape | null) => {
  if (modelKey && dataSources && Object.prototype.hasOwnProperty.call(dataSources, modelKey)) {
    const currentLayerShape = dataSources[modelKey]
    const { layers } = currentLayerShape || {}
    if (layers && Array.isArray(layers) && currentLayer) {
      const found = getItemByKeyValue(layers, 'name', currentLayer)
      return found
    }  
  }
  return -1
}


function DraggableCornerHandle({
  corner,
  position,
  onMove
}: {
  corner: string
  position: [number, number]
  onMove: (corner: string, lat: number, lon: number) => void
}) {
  return (
    <Marker
      position={position}
      draggable={true}
      icon={L.divIcon({
        className: styles.resizeHandle,
        iconSize: [14, 14],
        iconAnchor: [7, 7]
      })}
      eventHandlers={{
        dragend: (e) => {
          const marker = e.target
          const latlng = marker.getLatLng()
          onMove(corner, latlng.lat, latlng.lng)
        }
      }}
    />
  )
}

const snap = (value: number, step = 0.1) =>
  +(Math.round(value / step) * step).toFixed(6)

function EditableInnerGrid({ gridData, setInnerBounds }: any) {
  const [bounds, setBounds] = useState<any>(null)

  useEffect(() => {
    if (!gridData?.properties) return

    setBounds({
      latMin: gridData.properties.innerLatMin,
      latMax: gridData.properties.innerLatMax,
      lonMin: gridData.properties.innerLonMin,
      lonMax: gridData.properties.innerLonMax
    })
  }, [gridData])

  if (!bounds) return null

  const updateCorner = (corner: string, lat: number, lon: number) => {
    setBounds((prev: any) => {
      const next = { ...prev }

      const snappedLat = snap(lat, 0.1)
      const snappedLon = snap(lon, 0.1)

      if (corner.includes("n")) next.latMax = snappedLat
      if (corner.includes("s")) next.latMin = snappedLat
      if (corner.includes("e")) next.lonMax = snappedLon
      if (corner.includes("w")) next.lonMin = snappedLon

      setInnerBounds?.(next)
      return next
    })
  }

  const corners = [
    {
      id: "nw",
      position: [bounds.latMax, bounds.lonMin]
    },
    {
      id: "ne",
      position: [bounds.latMax, bounds.lonMax]
    },
    {
      id: "se",
      position: [bounds.latMin, bounds.lonMax]
    },
    {
      id: "sw",
      position: [bounds.latMin, bounds.lonMin]
    }
  ]

  return (
    <>
      <Rectangle
        bounds={[
          [bounds.latMin, bounds.lonMin],
          [bounds.latMax, bounds.lonMax]
        ]}
        pathOptions={{
          color: "#00ff00",
          weight: 2,
          fill: true,
          fillColor: "#00ff00",
          fillOpacity: 0.08
        }}
      />

      {corners.map((corner) => (
        <DraggableCornerHandle
          corner="nw"
          position={[bounds.latMax, bounds.lonMin]}
          onMove={updateCorner}
        />
      ))}
    </>
  )
}


export type EditableGridPoint = {
  hidden?: boolean
  id: string
  cenLat: number
  cenLon: number
  bounds?: EditableGridBoundsState
}

type SelectionParamsWithGridPoints = {
  activePointId?: string | null
  gridPoints?: EditableGridPoint[]
  outerBounds?: EditableGridBoundsState["outerBounds"]
  innerBounds?: EditableGridBoundsState["innerBounds"]
  centerLat?: number
  centerLon?: number
  centerPoints?: EditableGridBoundsState["centerPoints"]
}

interface SelectedAreaLayerProps {
  // onFeatureClick: (feature: FeatureCollection | null) => void
  suppressNextMapClickRef?: React.MutableRefObject<boolean>
}

export const SelectedAreaLayer = ({
  suppressNextMapClickRef
}: SelectedAreaLayerProps ) => {
  const [extremeFeatures, setExtremeFeatures] = useState<Feature[]>([])

  const { 
    dataSourceModel,
    showMax, 
    extremeFeatureMaxValue,
    extremeFeatureMinValue,
    showMin,
    meshDataGeoJson, 
    currentLayer,
    currentMapCenter,
    currentSelectionParams,
    getFeatureLocation,
    zoom,
    gridPoints,
    setGridPoints,
    setCurrentSelectionParams,
    activePointId,
  } = useContext(MapDataContext);

  const {
    visibleLayerLocationShapes,
    visibleLocationFullSummary,
  } = useContext(LocationsContext)

  const geoJSONLayerData:(FeatureCollection | null) = useMemo(() => {
    let features: Feature[] = []
    if (visibleLayerLocationShapes  && visibleLayerLocationShapes?.length > 0) {
      let featuresAreas: Feature[] = []
      visibleLayerLocationShapes.forEach(d => {
        if (d?.hidden !== true) {
          if (d.features) {
            const {features} = d
            const {color} = d
            const {properties} = features
            if (properties) {
              const foundName = Object.keys(properties).find(k => typeof properties[k] === 'string') 
              const name = foundName ? properties[foundName] : (properties.name || '')
              if (name) {
                features.properties = {
                  ...properties,
                  name
                }  
              }
            }
            features.properties = {...properties, color}
            featuresAreas = [...featuresAreas, features]
          } else if (d.data && d.data.features) {
            featuresAreas = [...featuresAreas, ...d.data.features]
          } else if (d.geom) {
            featuresAreas = [...featuresAreas, convertGeomToGeoJSON(d.geom)]
          }
        }
      })
      features = [...featuresAreas]
    }
    if (meshDataGeoJson) {
      const highlight = meshDataGeoJson.features.map(f => {
        return {
          ...f,
          properties: {
            ...f.properties,
            highlight: true
          }
        }
      })
      features = [...features, ...highlight]
  
    }
    
    if (features.length > 0) {
      return {
        "type": "FeatureCollection",
        "features": features
      }  
    }
    return null
  }, [
    meshDataGeoJson, 
    visibleLayerLocationShapes, 
  ])

  useEffect(() => {
    if (currentLayer && dataSourceModel && visibleLocationFullSummary) {
      let extremeFeatureMinForShape: Feature[] = []
      let extremeFeatureMaxForShape: Feature[] = []

      const {shape: {uid}, geoms} = visibleLocationFullSummary
      if (showMin) {
        extremeFeatureMinForShape = [...extremeFeatureMinForShape, ...getMinMaxValuesFromGeomShape({currentLayer, dataSourceModel, data: geoms, type: 'min'})]
      } else if (showMax) {
        extremeFeatureMaxForShape  = [...extremeFeatureMaxForShape, ...getMinMaxValuesFromGeomShape({currentLayer, dataSourceModel, data: geoms, type: 'max'})]
      }  
      setExtremeFeatures([...extremeFeatureMinForShape, ...extremeFeatureMaxForShape])
    }
  }, [visibleLocationFullSummary, currentLayer, dataSourceModel, showMin, showMax])

  const styleFeatureExtremes = useCallback((feature: any) => {
    let fillColor = ''

    const isExtreme = !!feature.properties.extreme

    const highlight = feature.properties.highlight === true
    // let useColour = highlight ? '#00ff00' : '#ffff00';
    let useColour;
    if (highlight && isExtreme === true && (extremeFeatureMaxValue !== null || extremeFeatureMinValue !== null)) {
      useColour = feature.properties.isMinValue? '#00ff00' : '#ff0000'
      const coordKey = feature?.properties.id
      const isHighlightedMax = coordKey === extremeFeatureMaxValue
      const isHighlightedMin = coordKey === extremeFeatureMinValue
      if (isHighlightedMax || isHighlightedMin) {
        fillColor = isHighlightedMax ? '#ff0000' : '#0ff000'
      }
    } else {
      // fillColor = valuesToColours(value, min, max, currentUnits?.reverse || false, cmoceanGrey)
      useColour =  ''
    }

    const showFill = fillColor ? true : false
    return {
      // filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 1))',
      color: !showFill ? useColour : '',
      stroke: true,
      fill: showFill,
      fillColor,
      weight: !showFill ? (isExtreme ? 2 : 2) : 2,
      opacity: 1,
      fillOpacity: 1,
    }
  }, [
    currentLayer,
    extremeFeatureMaxValue,
    extremeFeatureMinValue,
    JSON.stringify(dataSourceModel)
  ])

  
  const styleFeature = useCallback((feature: any) => {
    const fillColor = '#ffffff'
    
    const isPoint = feature.geometry.type === 'Point'
    const isLine = feature.geometry.type === 'LineString' || feature.geometry.type === 'MultiLineString'
    if (isPoint) {
      return {
        fill: true,
        stroke: false,
        fillColor: feature?.properties?.color || "#ffffff",
        // color: "#ff0000",
        weight: 2,
        fillOpacity: 1,
    }
    }
    const highlight = feature.properties.highlight === true
    // let useColour = highlight ? '#00ff00' : '#ffff00';
    const useColour = 'rgba(255,255,255,1)';

    const showFill = fillColor ? true : false
    return {
      filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 1))',
      // color: !showFill ? useColour : '',
      color: feature?.properties?.color || useColour,
      stroke: true,
      fill: showFill,
      fillColor,
      weight: 2,
      opacity: 1,
      fillOpacity: !isLine? 0.2: 0,
    }
  }, [])

  const activePoint = useMemo(() => {
    if (!activePointId) return null
    return gridPoints.find((point) => point.id === activePointId) ?? null
  }, [activePointId, gridPoints])

  useEffect(() => {
    if (!activePoint) return

    setCurrentSelectionParams((prev: SelectionParamsWithGridPoints | null) => {
      if (
        prev?.activePointId === activePoint.id &&
        prev?.outerBounds === activePoint.bounds?.outerBounds &&
        prev?.innerBounds === activePoint.bounds?.innerBounds
      ) {
        return prev
      }

      return {
        ...prev,
        activePointId: activePoint.id,
        outerBounds: activePoint.bounds?.outerBounds,
        innerBounds: activePoint.bounds?.innerBounds,
        gridPoints
      }
    })
  }, [activePoint, gridPoints])

  const hasEditableGridPoints = dataSourceModel?.model === 'ondemand' && gridPoints.length > 0
  
  const visibleGridPoints = hasEditableGridPoints ? gridPoints.filter((point) => !point.hidden) : []
  const inactiveGridPoints = visibleGridPoints.filter((point) => point.id !== activePointId)
  const activeGridPoint = visibleGridPoints.find((point) => point.id === activePointId)

  return (
    <>
        {[...inactiveGridPoints, activeGridPoint].filter(Boolean).map((point) => {
          const isActive = point.id === activePointId
          
          if (point.hidden) return null;

          return (
            <EditableGridOverlay
              addPointOnClick={false}
              key={point.id}
              suppressNextMapClickRef={suppressNextMapClickRef}
              centerLat={point.cenLat}
              centerLon={point.cenLon}
              bounds={point.bounds}
              inactive={!isActive}
              onBoundsUpdate={(outerBounds, innerBounds, centerLat, centerLon, centerPoints) => {
                if (!isActive) return
              
                const updatePoint = (gridPoint: EditableGridPoint) => {
                  if (gridPoint.id !== point.id) return gridPoint
              
                  return {
                    ...gridPoint,
                    cenLat: centerLat,
                    cenLon: centerLon,
                    bounds: {
                      outerBounds,
                      innerBounds,
                      centerLat,
                      centerLon,
                      centerPoints
                    }
                  }
                }
              
                setGridPoints((prev: EditableGridPoint[] = []) => {
                  const source = prev.length ? prev : gridPoints
                  return source.map(updatePoint)
                })
              
                setCurrentSelectionParams((prev: SelectionParamsWithGridPoints | null) => {
                  const previousGridPoints = prev?.gridPoints?.length
                    ? prev.gridPoints
                    : gridPoints
              
                  const nextGridPoints = previousGridPoints.map(updatePoint)
              
                  return {
                    ...prev,
                    activePointId: point.id,
                    outerBounds,
                    innerBounds,
                    centerLat,
                    centerLon,
                    centerPoints,
                    gridPoints: nextGridPoints
                  }
                })
              }}
            />
          )
        })}
      {geoJSONLayerData && (
        <>
          <GeoJSON
            // @ts-ignore 
            key={nanoid()}
            attribution=""
            data={geoJSONLayerData}
            pointToLayer={(feature, point) => {
              return L.circleMarker(point, {
                radius: 6
              });
            }}
            onEachFeature={(feature: any, layer: any) => {
              if (feature.properties.name || feature.properties.NAME) {
                layer.bindTooltip(
                  feature.properties.name || feature.properties.NAME || '',
                  {
                      className: styles.shapeLabel
                  }
                );
              }
              layer.on('mouseout', () => {})
            }}
            style={styleFeature}
          />
          <GeoJSON
            // @ts-ignore 
            key={nanoid()}
            attribution=""
            data={
              {
                "type": "FeatureCollection",
                "features": extremeFeatures
              } as FeatureCollection
            }
            style={styleFeatureExtremes}
          />
        </>
      )}
    </>
  );
};